import { useRef, useMemo } from "react";
import { X, Printer } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * PrintView - Formats the task board for clean printing on 8.5 x 11 paper
 * 
 * Features:
 * - Portrait layout optimized for letter paper
 * - Groups tasks by column (TO DO, DOING, DONE) and category (ACTION, EMAILS, CALLS)
 * - Clean black/gray text on white background
 * - 14pt+ font for readability
 * - Calm, minimal, thoughtful daily plan aesthetic
 */

// Category display order and labels
const CATEGORY_ORDER = ['action', 'emails', 'calls', 'uncategorized'];
const CATEGORY_LABELS = {
  action: 'ACTION',
  emails: 'EMAILS', 
  calls: 'CALLS',
  uncategorized: 'OTHER'
};

// Column display order
const COLUMN_ORDER = ['TO DO', 'DOING', 'DONE'];

const PrintView = ({ isOpen, onClose, lists }) => {
  const printRef = useRef(null);
  
  // Extract the actual lists array from the data structure
  const actualLists = useMemo(() => {
    if (!lists) return [];
    // Handle the nested structure: data.boards[0].lists
    if (lists.boards && lists.boards.length > 0 && lists.boards[0].lists) {
      return lists.boards[0].lists.map(list => ({
        id: list.id,
        title: list.name,
        items: list.cards || []
      }));
    }
    // Fallback if it's already an array
    if (Array.isArray(lists)) {
      return lists;
    }
    return [];
  }, [lists]);
  
  // Format current date
  const formatDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };
  
  // Group tasks by category within a column
  const groupTasksByCategory = (tasks) => {
    const grouped = {
      action: [],
      emails: [],
      calls: [],
      uncategorized: []
    };
    
    tasks.forEach(task => {
      // Map card categories to our display categories
      let category = 'uncategorized';
      const taskCategory = task.category?.toLowerCase();
      
      if (taskCategory === 'action' || taskCategory === 'task') {
        category = 'action';
      } else if (taskCategory === 'email' || taskCategory === 'emails') {
        category = 'emails';
      } else if (taskCategory === 'phone' || taskCategory === 'calls' || taskCategory === 'call') {
        category = 'calls';
      }
      
      grouped[category].push({
        ...task,
        // Normalize the task display properties
        content: task.content || task.title || task.text,
        important: task.isCritical || task.important
      });
    });
    
    return grouped;
  };
  
  // Get columns in the correct order
  const getOrderedColumns = () => {
    if (!actualLists || actualLists.length === 0) return [];
    
    const orderedColumns = [];
    
    COLUMN_ORDER.forEach(columnName => {
      const column = actualLists.find(list => 
        list.title.toUpperCase() === columnName || 
        list.title.toUpperCase().includes(columnName)
      );
      if (column && column.items && column.items.length > 0) {
        orderedColumns.push(column);
      }
    });
    
    // Add any other columns not in the standard order
    actualLists.forEach(list => {
      if (!orderedColumns.find(c => c.id === list.id) && list.items && list.items.length > 0) {
        orderedColumns.push(list);
      }
    });
    
    return orderedColumns;
  };
  
  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Where was i — Today's Thread</title>
        <style>
          @page {
            size: 8.5in 11in;
            margin: 0.6in 0.75in;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14pt;
            line-height: 1.5;
            color: #1a1a1a;
            background: white;
            padding: 0.5in;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 28pt;
            padding-bottom: 16pt;
            border-bottom: 1px solid #e5e5e5;
          }
          
          .print-title {
            font-size: 20pt;
            font-weight: 600;
            color: #0f0f0f;
            margin-bottom: 6pt;
            letter-spacing: -0.02em;
          }
          
          .print-date {
            font-size: 11pt;
            color: #6b6b6b;
            font-weight: 400;
          }
          
          .column-section {
            margin-bottom: 24pt;
            page-break-inside: avoid;
          }
          
          .column-header {
            font-size: 16pt;
            font-weight: 700;
            color: #0f0f0f;
            margin-bottom: 12pt;
            padding-bottom: 6pt;
            border-bottom: 2px solid #e5e5e5;
          }
          
          .category-section {
            margin-bottom: 16pt;
            margin-left: 8pt;
          }
          
          .category-label {
            font-size: 10pt;
            font-weight: 600;
            color: #888;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 6pt;
          }
          
          .task-list {
            list-style: none;
            margin-left: 4pt;
          }
          
          .task-item {
            font-size: 14pt;
            color: #1a1a1a;
            margin-bottom: 6pt;
            padding-left: 16pt;
            position: relative;
            line-height: 1.4;
          }
          
          .task-item::before {
            content: "•";
            position: absolute;
            left: 0;
            color: #666;
          }
          
          .task-item.important {
            font-weight: 500;
          }
          
          .task-item.important::before {
            color: #d97706;
            content: "★";
            font-size: 10pt;
          }
          
          .task-item.done {
            text-decoration: line-through;
            color: #888;
          }
          
          .task-item.done::before {
            color: #aaa;
          }
          
          .empty-message {
            font-size: 12pt;
            color: #999;
            font-style: italic;
            margin-left: 8pt;
          }
          
          .footer {
            margin-top: 32pt;
            padding-top: 12pt;
            border-top: 1px solid #e5e5e5;
            text-align: center;
            font-size: 9pt;
            color: #aaa;
          }
          
          @media print {
            body {
              padding: 0;
            }
            .column-section {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  
  const orderedColumns = getOrderedColumns();
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h2 className="text-lg font-semibold text-stone-800">Print Preview</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-[#F58227] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#D96D19] transition-colors"
                  data-testid="print-button"
                >
                  <Printer size={18} />
                  Print
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                  data-testid="close-print-preview"
                >
                  <X size={20} className="text-stone-500" />
                </button>
              </div>
            </div>
            
            {/* Print Preview Content */}
            <div className="overflow-y-auto flex-1 p-6 bg-stone-50">
              <div 
                ref={printRef}
                className="bg-white rounded-lg shadow-sm p-8 mx-auto"
                style={{ 
                  maxWidth: '7.5in',
                  minHeight: '10in',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
              >
                {/* Header */}
                <div className="print-header text-center mb-7 pb-4 border-b border-stone-200">
                  <h1 className="print-title text-xl font-semibold text-stone-900 mb-1">
                    Where was i — Today's Thread
                  </h1>
                  <p className="print-date text-sm text-stone-500">
                    {formatDate()}
                  </p>
                </div>
                
                {/* Columns */}
                {orderedColumns.length > 0 ? (
                  orderedColumns.map(column => {
                    const groupedTasks = groupTasksByCategory(column.items);
                    const hasAnyTasks = Object.values(groupedTasks).some(arr => arr.length > 0);
                    const isDoneColumn = column.title.toUpperCase().includes('DONE');
                    
                    if (!hasAnyTasks) return null;
                    
                    return (
                      <div key={column.id} className="column-section mb-6">
                        <h2 className="column-header text-base font-bold text-stone-900 mb-3 pb-1.5 border-b-2 border-stone-200">
                          {column.title.toUpperCase()}
                        </h2>
                        
                        {CATEGORY_ORDER.map(category => {
                          const tasks = groupedTasks[category];
                          if (!tasks || tasks.length === 0) return null;
                          
                          return (
                            <div key={category} className="category-section mb-4 ml-2">
                              <p className="category-label text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1.5">
                                {CATEGORY_LABELS[category]}
                              </p>
                              <ul className="task-list ml-1">
                                {tasks.map(task => (
                                  <li 
                                    key={task.id} 
                                    className={`task-item text-sm mb-1.5 pl-4 relative ${task.important ? 'font-medium' : ''} ${isDoneColumn ? 'done text-stone-400 line-through' : 'text-stone-800'}`}
                                  >
                                    <span className={`absolute left-0 ${isDoneColumn ? 'text-stone-300' : task.important ? 'text-amber-500' : 'text-stone-400'}`}>
                                      {task.important ? '★' : '•'}
                                    </span>
                                    {task.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                ) : (
                  <p className="empty-message text-sm text-stone-400 italic ml-2">
                    No tasks to display. Add some tasks to see them here.
                  </p>
                )}
                
                {/* Footer */}
                <div className="footer mt-8 pt-3 border-t border-stone-200 text-center">
                  <p className="text-[9px] text-stone-400">
                    Generated from Where was i • wherewasi.app
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrintView;
